import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  List,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useSnackbar } from 'notistack';
import {
  useStartListGroups,
  useCreateStartListGroup,
  useUpdateStartListGroup,
  useDeleteStartListGroup,
  useResetStartListGroupsFromTemplate,
} from '../../../api/hooks/useStartListGroups';
import { StartListGroupEditor } from '../../../components/startlist/StartListGroupEditor';
import { StartListGroupRow } from '../../../components/startlist/StartListGroupRow';
import { ResetFromTemplateButton } from '../../../components/startlist/ResetFromTemplateButton';
import { ConfirmDialog } from '../../../components/ConfirmDialog';
import type { StartListGroup, StartListTarget } from '../../../types/startList';

interface Props {
  disciplineId: number;
  disciplineName?: string;
  isIndividual?: boolean;
}

export function StartListConfigTab({ disciplineId, disciplineName, isIndividual }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const { data: groups = [], isLoading, error } = useStartListGroups(disciplineId);
  const createMut = useCreateStartListGroup(disciplineId);
  const updateMut = useUpdateStartListGroup(disciplineId);
  const deleteMut = useDeleteStartListGroup(disciplineId);
  const resetMut = useResetStartListGroupsFromTemplate(disciplineId);

  const [editing, setEditing] = useState<StartListGroup | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<StartListGroup | null>(null);

  const defaultTarget: StartListTarget = isIndividual ? 'INDIVIDUAL' : 'TEAM';

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ my: 2 }}>
        Nepodařilo se načíst startovací skupiny.
      </Alert>
    );
  }

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            Startovací skupiny{disciplineName ? ` — ${disciplineName}` : ''}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Skupiny určují, jak budou závodníci/družstva rozděleni do rozběhů.
            Každá skupina má svůj počet paralelních drah.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <ResetFromTemplateButton
            loading={resetMut.isPending}
            onConfirm={() => {
              resetMut.mutate(undefined, {
                onSuccess: (data) => {
                  enqueueSnackbar(`Skupiny obnoveny ze šablony (${data.length} sk.)`, { variant: 'success' });
                },
                onError: () => enqueueSnackbar('Chyba pri resetu skupin', { variant: 'error' }),
              });
            }}
          />
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setCreating(true)}
          >
            Přidat skupinu
          </Button>
        </Stack>
      </Stack>

      {groups.length === 0 ? (
        <Alert severity="info">
          Tato disciplína zatím nemá žádné startovací skupiny. Přidejte první nebo resetujte na šablonu.
        </Alert>
      ) : (
        <List dense disablePadding>
          {groups.map((g) => (
            <StartListGroupRow
              key={g.id}
              group={g}
              onEdit={() => setEditing(g)}
              onDelete={() => setDeleting(g)}
            />
          ))}
        </List>
      )}

      <StartListGroupEditor
        open={creating || !!editing}
        group={editing}
        loading={createMut.isPending || updateMut.isPending}
        defaultTarget={defaultTarget}
        onCancel={() => {
          setCreating(false);
          setEditing(null);
        }}
        onSubmit={(req) => {
          if (editing) {
            updateMut.mutate(
              { id: editing.id, req },
              {
                onSuccess: () => {
                  setEditing(null);
                  enqueueSnackbar('Skupina upravena', { variant: 'success' });
                },
                onError: (err: unknown) => {
                  const e = err as { response?: { data?: { message?: string } } };
                  enqueueSnackbar(e?.response?.data?.message ?? 'Chyba při úpravě', { variant: 'error' });
                },
              },
            );
          } else {
            createMut.mutate(req, {
              onSuccess: () => {
                setCreating(false);
                enqueueSnackbar('Skupina vytvořena', { variant: 'success' });
              },
              onError: (err: unknown) => {
                const e = err as { response?: { data?: { message?: string } } };
                enqueueSnackbar(e?.response?.data?.message ?? 'Chyba při vytváření', { variant: 'error' });
              },
            });
          }
        }}
      />

      <ConfirmDialog
        open={!!deleting}
        title="Smazat startovací skupinu?"
        message={
          deleting
            ? `Skupina "${deleting.name}" bude trvale smazána. Pokračovat?`
            : ''
        }
        confirmText="Smazat"
        destructive
        loading={deleteMut.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={() => {
          if (!deleting) return;
          deleteMut.mutate(deleting.id, {
            onSuccess: () => {
              setDeleting(null);
              enqueueSnackbar('Skupina smazana', { variant: 'success' });
            },
            onError: () => enqueueSnackbar('Chyba pri mazani', { variant: 'error' }),
          });
        }}
      />
    </Box>
  );
}
